import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppConfig } from '../config';
import { env } from '../config/env';
import { isKioskShell } from '../config/kioskShell';
import { selectKioskCamera } from '../capture/selectKioskCamera';
import { prepareARModule, startArWarm } from '../ar';
import { warmMouthDetector } from '../ar/mouthOpenDetector';
import { warmLogo, probeMimeType } from '../capture';
import { requestMicrophone, requestPermissions } from '../capture';
import { assets } from '../assets';
import { ArReadyDot } from '../components/ArReadyDot';
import { DevHud } from '../components/DevHud';
import { warmPersistentVideo } from '../components/PersistentVideo';
import { AttractScreen } from '../screens/AttractScreen';
import { CaptureScreen } from '../screens/CaptureScreen';
import { EndCardScreen } from '../screens/EndCardScreen';
import { ErrorScreen } from '../screens/ErrorScreen';
import { InstructionScreen } from '../screens/InstructionScreen';
import { PhoneEntryScreen } from '../screens/PhoneEntryScreen';
import { ResultScreen } from '../screens/ResultScreen';
import { noteError, noteEvent } from '../lib/diagnostics';
import { enqueueClip, startQueue } from '../upload';
import { useActivation } from './useActivation';
import { useIdleTimeout } from './useIdleTimeout';
import { devWarn } from '../lib/devLog';

/**
 * How long an error screen stays up before the unit shows attract again.
 *
 * Long enough for a guest to read what happened, short enough that the next
 * person does not find someone else's failure. Deliberately shorter than the
 * 30s idle watchdog, and unlike it, not reset by tapping Retry.
 */
const ERROR_RESET_MS = 10_000;

/**
 * Kiosk sequence:
 *   attract → instruction → capture → result → phone → end → attract
 *
 * No permission gate and no in-app browser check: the native shell has already
 * granted camera and mic, and there is no webview to escape.
 *
 * Every screen past the attract loop is on an idle watchdog. A guest who walks
 * off must not leave their face, their clip, or their phone number up for the
 * next person.
 */
export function KioskFlow({ config }: { config: AppConfig }) {
  const a = useActivation(config, 'attract');
  const cameraKit = env.arModule === 'camera-kit';

  /**
   * The panel's camera, resolved from the native shell's Camera2 census rather
   * than baked into the build — a MediaDevices deviceId is salted per origin and
   * per WebView profile, so it cannot be known at build time and a stale one
   * opens nothing. See selectKioskCamera.ts for why getting this wrong is worse
   * than an inconvenience on this particular board.
   */
  const [resolvedCameraId, setResolvedCameraId] = useState<string | undefined>();

  const resolveCamera = useCallback(async (): Promise<string | undefined> => {
    if (config.cameraDeviceId) return config.cameraDeviceId; // explicit override
    if (resolvedCameraId) return resolvedCameraId;
    if (!isKioskShell()) return undefined; // laptop demo of the kiosk flow
    const { deviceId } = await selectKioskCamera();
    setResolvedCameraId(deviceId);
    return deviceId;
  }, [config.cameraDeviceId, resolvedCameraId]);

  // Resolve on the attract screen so the tap does not pay for it. Enumeration
  // does not open a device, so this is safe to do before any guest is present.
  useEffect(() => {
    if (!isKioskShell() || config.cameraDeviceId) return;
    void resolveCamera().catch((err) => noteError('camera.select', err));
    // Once per mount: the device list does not change under a bolted-down panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** What the screens see — `config` with the resolved camera folded in. */
  const effectiveConfig = useMemo<AppConfig>(
    () => (resolvedCameraId ? { ...config, cameraDeviceId: resolvedCameraId } : config),
    [config, resolvedCameraId],
  );

  /**
   * The native shell's "back" and its RESET broadcast both land here — see
   * MainActivity.resetToAttract(). The panel has no back button, so this is an
   * operator affordance for getting a stuck session home without a reload.
   */
  useEffect(() => {
    const w = window as unknown as { __kioskReset?: () => void };
    w.__kioskReset = () => {
      noteEvent('reset requested by the shell');
      a.resetSession('attract');
    };
    return () => {
      delete w.__kioskReset;
    };
  }, [a]);

  // The durable delivery queue starts with the app and outlives every session,
  // including the ones interrupted by a reload or a crash.
  useEffect(() => {
    void startQueue();
  }, []);

  // The instruction clip is fetched and decoded while the attract loop plays,
  // so the tap lands on a first frame that is already there. The attract loop
  // warms itself by being the first thing on screen. See PersistentVideo.
  useEffect(() => {
    warmPersistentVideo('instruction', assets.instruction);
  }, []);

  // A retake is a second capture screen with no instruction clip to hide the
  // connect behind. Have the link ready while the guest watches their clip;
  // if they move on instead, the next guest's attract tap reuses it.
  useEffect(() => {
    if (a.step === 'result') prepareARModule();
  }, [a.step]);

  // Camera Kit's own warm-up is started in main.tsx, before React renders, and
  // retries on its own — see src/ar/cameraKitWarm.ts. startArWarm() is
  // idempotent, so calling it again here simply covers a remount without
  // restarting anything that is already in flight.
  useEffect(() => {
    // Recording happens on this device whichever module renders the AR, so the
    // recorder's warm-ups are gated on "not the stub" rather than on Camera Kit.
    // They used to sit behind the Camera Kit check, and the remote build paid
    // for the logo knockout at compositor attach and never proved a container.
    if (env.arModule !== 'stub') {
      // ~630KB and a 1.5M-pixel knockout pass; pay for it here, not at the
      // trigger. Cached for the life of the page.
      //
      // The container probe waits for it. The knockout blocks the main thread
      // for the better part of a second, and a probe running underneath it gets
      // no paints and so no bytes — on 2026-09-18 that failed VP8 and "proved"
      // VP9, the container the kiosk list puts second because it costs twice
      // the CPU to encode. Sequenced, each gets the thread to itself.
      void warmLogo()
        .catch(() => null)
        .then(() => probeMimeType())
        .then((type) => {
          noteEvent(`recorder container proven: ${type ?? 'NONE — no container produced bytes'}`);
        });
    }
    if (!cameraKit) return;
    startArWarm();
    void warmMouthDetector().catch((err) => {
      devWarn('[kiosk] MediaPipe warm failed — will retry at capture', err);
    });
  }, [cameraKit]);

  useIdleTimeout({
    // The live camera gets a longer leash — see AppConfig.captureIdleTimeoutMs.
    timeoutMs: a.step === 'capture' ? config.captureIdleTimeoutMs : config.idleTimeoutMs,
    enabled: a.step !== 'attract',
    onIdle: () => {
      noteEvent('idle timeout — returning to attract');
      a.resetSession('attract');
    },
  });

  /**
   * An error screen returns to attract on its own, faster than the idle
   * watchdog would and regardless of whether anyone is prodding it.
   *
   * The idle timeout already covers a guest who walks away, but not the case
   * this exists for: the AR engine is down, Retry re-enters the same failure,
   * and every tap resets the idle clock. Without this the unit parks on one
   * guest's error and the next five people walk up to it.
   */
  useEffect(() => {
    if (a.step !== 'error') return;
    const t = window.setTimeout(() => {
      noteEvent('error screen timed out — returning to attract');
      a.resetSession('attract');
    }, ERROR_RESET_MS);
    return () => clearTimeout(t);
  }, [a.step, a]);

  /**
   * Open the devices the moment the instruction clip starts, not when the guest
   * arrives at the camera.
   *
   * The clip asks nothing of the hardware we need, so it is free cover for the
   * one genuinely slow step in the flow: getUserMedia on the panel. Paying for
   * it at the capture screen instead is what put an "Opening the camera…" card
   * between the clip ending and the guest seeing themselves.
   *
   * How much cover that is shrank hard on 2026-09-17: the recut runs 3.1s where
   * the original ran 19.7s. The head start is still worth taking — it is the
   * difference between opening the device early and opening it late — but it is
   * no longer long enough to assume the camera is up by the time the guest
   * reaches capture. Whether "Opening the camera…" comes back on the panel is
   * now a question for the unit, not something this comment can promise; see
   * `timing.openingNoticeAfterMs`.
   *
   * The web flow has always worked this way — its permission step opens both
   * devices before the same clip plays — so this is the kiosk catching up rather
   * than a new mechanism. The live stream is handed to CaptureScreen, and
   * Camera Kit reuses it instead of opening the camera a second time.
   *
   * Both devices are pre-granted by the shell, so nothing is prompted. A mic
   * that fails still leaves a camera and a silent clip; a camera that fails
   * lands on the error screen at capture exactly as it did before.
   */
  function begin() {
    a.goTo('instruction');

    // Remote AR: negotiate the stream now, under the instruction clip, so the
    // capture screen mounts onto a live picture. No-op for the other modules.
    prepareARModule();

    // Only Camera Kit adopts a stream opened elsewhere. The stub and the vendor
    // module both call getUserMedia themselves at capture, so opening one here
    // would cycle the device for nothing — and a camera closed and reopened
    // inside a second is exactly the sequence Android is worst at.
    if (!cameraKit) {
      void requestMicrophone({ deviceId: config.micDeviceId }).then((result) => {
        a.setMic(result.stream, result.granted);
      });
      return;
    }

    void (async () => {
      // Usually already resolved on attract; awaited here for the case where a
      // guest taps before it finished. A failure leaves the id undefined, and
      // requireDeviceId then turns that into a clean error rather than an open
      // of whatever camera Chromium picks first.
      let cameraDeviceId: string | undefined;
      try {
        cameraDeviceId = await resolveCamera();
      } catch (err) {
        noteError('camera.select', err);
      }
      return requestPermissions({
        micDeviceId: config.micDeviceId,
        cameraDeviceId,
        requireDeviceId: config.requireExplicitCamera && isKioskShell(),
      });
    })().then((result) => {
      a.setMic(result.micStream, result.micGranted);
      a.setCameraStream(result.cameraStream);
      noteEvent(
        `devices opened during instruction (camera ${result.cameraGranted ? 'yes' : 'NO'}, mic ${
          result.micGranted ? 'yes' : 'no'
        }${result.cameraStream ? ', handed to capture' : ''})`,
      );
    });
  }

  return (
    <>
      <DevHud mode="kiosk" step={a.step} />
      <ArReadyDot />

      {a.step === 'attract' ? <AttractScreen onStart={begin} /> : null}

      {a.step === 'instruction' ? (
        <InstructionScreen config={effectiveConfig} onDone={() => a.goTo('capture')} />
      ) : null}

      {a.step === 'capture' ? (
        <CaptureScreen
          config={effectiveConfig}
          micStream={a.micStream}
          // Opened back on the attract tap and live throughout the instruction
          // clip — Camera Kit adopts it rather than reopening the device.
          cameraStream={a.cameraStream}
          onCaptured={(clip) => {
            a.acceptClip(clip);
            a.completeSession();
            a.goTo('result');
          }}
          onFailed={a.fail}
        />
      ) : null}

      {a.step === 'result' ? (
        <ResultScreen
          config={effectiveConfig}
          clip={a.clip}
          clipUrl={a.clipUrl}
          micGranted={a.micGranted}
          onRetake={a.retake}
          onNext={() => a.goTo('phone')}
        />
      ) : null}

      {a.step === 'phone' ? (
        <PhoneEntryScreen
          shareCode={a.shareCode}
          onSubmit={(e164, consent) => {
            // Optimistic on purpose: the record going to disk is the promise,
            // and the queue keeps it. Nothing here waits on the network.
            if (a.clip) {
              void enqueueClip({
                sessionId: a.sessionId,
                blob: a.clip.blob,
                mimeType: a.clip.mimeType,
                phone: e164,
                smsConsent: consent,
                shareCode: a.shareCode,
              });
            }
            a.goTo('endCard');
          }}
          // Taking the code is not the same as declining: the clip still has to
          // be uploaded, or the code the guest just copied down resolves to
          // nothing. Same queue, no phone number, no consent.
          onUseCode={() => {
            if (a.clip) {
              void enqueueClip({
                sessionId: a.sessionId,
                blob: a.clip.blob,
                mimeType: a.clip.mimeType,
                phone: '',
                smsConsent: false,
                shareCode: a.shareCode,
              });
            }
            a.goTo('endCard');
          }}
        />
      ) : null}

      {a.step === 'endCard' ? (
        <EndCardScreen config={config} onRestart={() => a.resetSession('attract')} />
      ) : null}

      {a.step === 'error' ? (
        <ErrorScreen config={config} error={a.error} onRetry={() => a.goTo('capture')} />
      ) : null}
    </>
  );
}
